import React, { useState, useEffect } from "react";
import "./ManagePagoView.css";

const API_URL = process.env.REACT_APP_API_URL;

export default function ManagePagoView() {
  const [pagos, setPagos] = useState([]);
  const [precioBinance, setPrecioBinance] = useState(null);
  const [binanceLoading, setBinanceLoading] = useState(false);
  const [binanceError, setBinanceError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [procesando, setProcesando] = useState(null);
  const [error, setError] = useState(null);
  const [showRechazoModal, setShowRechazoModal] = useState(false);
  const [pagoARechazar, setPagoARechazar] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  const token = localStorage.getItem("access_token");

  // Fetch Binance P2P URL from Config API
  const fetchBinanceP2P = async () => {
    setBinanceLoading(true);
    setBinanceError(null);
    try {
      // Llamar al backend que tiene CORS configurado
      const response = await fetch(`${API_URL}/pagos/binance_precio`);
      const data = await response.json();

      if (data.success && data.data) {
        const precio = data.data.precio_usdt_ves;
        setPrecioBinance({
          offers: [
            {
              payType: data.data.pay_type || "BNC",
              price: precio,
              merchant: data.data.merchant || "N/A",
              minAmount: data.data.min_amount || 0,
              maxAmount: data.data.max_amount || 0,
              available: data.data.available_quantity || 0,
            },
          ],
          timestamp: new Date().toISOString(),
        });
      } else {
        setBinanceError("...");
      }
    } catch (err) {
      console.error("Binance P2P error:", err);
      setBinanceError(
        "No se pudo conectar. Consulta Binance P2P directamente."
      );
    } finally {
      setBinanceLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch pagos pendientes
      const pagosRes = await fetch(`${API_URL}/pagos/pendientes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (pagosRes.ok) {
        const pagosData = await pagosRes.json();
        setPagos(pagosData);
      } else {
        throw new Error("Error al cargar pagos");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchBinanceP2P(); // Cargar precios Binance P2P al iniciar
    const interval = setInterval(fetchData, 30000); // Actualizar pagos cada 30 segundos
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAprobar = async (pago, forceApprove = false) => {
    const esBinancePay = pago.tipo_pago === "binance_pay";
    const tipoMoneda = esBinancePay ? "Cartones" : "VES";
    const montoMostrar = esBinancePay
      ? Math.floor(pago.monto)
      : `${pago.monto} ${tipoMoneda}`;

    if (
      !forceApprove &&
      !window.confirm(
        `¿Aprobar pago de ${montoMostrar} cartones del usuario ${pago.usuario_email}?`
      )
    ) {
      return;
    }

    setProcesando(pago.id);
    try {
      const res = await fetch(`${API_URL}/pagos/procesar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reporte_id: pago.id,
          accion: "aprobar",
          force_approve: forceApprove,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        alert(
          `Pago aprobado.\nTickets: ${
            data.detalles?.cantidad_tickets
          }\nRemanente: ${data.detalles?.remanente_ves?.toFixed(2)} VES`
        );
        fetchData();
      } else {
        alert(`Error: ${data.detail}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcesando(null);
    }
  };

  const handleRechazarClick = (pago) => {
    setPagoARechazar(pago);
    setShowRechazoModal(true);
  };

  const handleConfirmarRechazo = async () => {
    if (!motivoRechazo.trim()) {
      alert("Debe especificar un motivo de rechazo");
      return;
    }

    setProcesando(pagoARechazar.id);
    setShowRechazoModal(false);

    try {
      const res = await fetch(`${API_URL}/pagos/procesar`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reporte_id: pagoARechazar.id,
          accion: "rechazar",
          motivo_rechazo: motivoRechazo,
        }),
      });

      if (res.ok) {
        alert("Pago rechazado correctamente");
        fetchData();
      } else {
        const data = await res.json();
        alert(`Error: ${data.detail}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setProcesando(null);
      setPagoARechazar(null);
      setMotivoRechazo("");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online":
        return "#4caf50";
      case "offline":
        return "#f44336";
      case "degraded":
        return "#ff9800";
      default:
        return "#9e9e9e";
    }
  };

  const getOverallColor = (overall) => {
    switch (overall) {
      case "healthy":
        return "#4caf50";
      case "degraded":
        return "#ff9800";
      case "critical":
        return "#f44336";
      default:
        return "#9e9e9e";
    }
  };

  return (
    <div className="manage-pago-container">
      {/* Sección Superior: Precios Binance P2P (desde navegador) */}
      <div
        className="admin-status-bar"
        style={{ flexDirection: "column", gap: "10px" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <h4 style={{ margin: 0 }}>Precios Binance P2P (USD VES)</h4>
          <button
            onClick={fetchBinanceP2P}
            className="refresh-mini-btn"
            disabled={binanceLoading}
            title="Actualizar precios Binance"
          >
            {binanceLoading ? "⏳" : "🔄"}
          </button>
        </div>

        {binanceError && (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ color: "#f44336", fontSize: "12px" }}>
              ⚠️ {binanceError}
            </span>
            <a
              href="https://p2p.binance.com/es/trade/buy/USDT?fiat=VES&payment=PagoMovil"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: "#f0b90b",
                color: "#000",
                padding: "4px 10px",
                borderRadius: "4px",
                textDecoration: "none",
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              Abrir Binance P2P ↗
            </a>
          </div>
        )}

        {precioBinance?.offers && (
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {precioBinance.offers.map((offer, idx) => (
              <div
                key={offer.payType}
                style={{
                  background: idx === 0 ? "#4caf50" : "#37474f",
                  color: "white",
                  padding: "4px 10px",
                  borderRadius: "4px",
                  fontSize: "12px",
                }}
              >
                <strong>{offer.payType}</strong>: {offer.price.toLocaleString()}{" "}
                VES
                {idx === 0 && <span> ⭐ Mejor</span>}
              </div>
            ))}
          </div>
        )}

        {precioBinance?.timestamp && (
          <div style={{ fontSize: "10px", color: "#999" }}>
            Actualizado:{" "}
            {new Date(precioBinance.timestamp).toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Contenedor Principal de Pagos */}
      <div className="pagos-main-card">
        <div className="pagos-header">
          <div className="header-title-group">
            <h3>Gestión de Pagos</h3>
            <span className="count-badge">{pagos.length}</span>
          </div>
          <div className="header-actions">
            <button
              onClick={fetchData}
              className="refresh-mini-btn"
              title="Refrescar pagos"
            >
              <span className={loading ? "spinning" : ""}>🔄</span>
            </button>
          </div>
        </div>

        {error && <div className="error-banner">⚠️ {error}</div>}

        <div className="pagos-scroll-area">
          {pagos.length === 0 && !loading ? (
            <div className="empty-state">
              <div className="empty-icon">📂</div>
              <p>No hay pagos pendientes por procesar</p>
            </div>
          ) : (
            pagos.map((pago) => (
              <div key={pago.id} className="pago-list-item">
                {/* Fila Superior: Usuario y Monto */}
                <div className="pago-main-info">
                  <div className="user-info">
                    <span className="email">{pago.usuario_email}</span>
                    <span className="user-id">
                      ID: {pago.usuario_id?.slice(-6)}
                    </span>
                  </div>
                  <div className="amount-info">
                    <span className="amount">
                      {pago.monto?.toLocaleString()} VES
                    </span>
                    <span className="bank-label">{pago.banco}</span>
                  </div>
                </div>

                {/* Fila Media: Detalles de la Transacción */}
                <div className="pago-transaction-details">
                  <div className="detail-pill">
                    <span className="label">REF:</span>
                    <span className="value">{pago.referencia}</span>
                  </div>
                  <div className="detail-pill">
                    <span className="label">TLF:</span>
                    <span className="value">{pago.telefono}</span>
                  </div>
                  <div className="detail-pill">
                    <span className="label">FECHA:</span>
                    <span className="value">
                      {pago.fecha
                        ? new Date(pago.fecha).toLocaleString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            day: "2-digit",
                            month: "2-digit",
                          })
                        : "N/A"}
                    </span>
                  </div>
                  {pago.precio_usdt_ves && (
                    <div
                      className="detail-pill"
                      style={{ background: "#e8f5e9" }}
                    >
                      <span className="label">USDT P2P:</span>
                      <span
                        className="value"
                        style={{ color: "#2e7d32", fontWeight: "bold" }}
                      >
                        {pago.precio_usdt_ves.toLocaleString()} VES
                      </span>
                    </div>
                  )}
                </div>

                {/* Fila de Cálculos (Look & Feel Profesional) */}
                {pago.tipo_pago === "binance_pay" ? (
                  <div className="pago-calc-row">
                    <div
                      className="calc-item"
                      style={{ background: "#e3f2fd" }}
                    >
                      <span>💳 Binance Pay</span>
                      <strong className="ticket-count">
                        {pago.monto} USDT
                      </strong>
                    </div>
                    <div className="calc-item">
                      <span>Tickets:</span>
                      <strong className="ticket-count">
                        {Math.floor(pago.monto)}
                      </strong>
                    </div>
                  </div>
                ) : (
                  pago.precio_usdt_ves && (
                    <div className="pago-calc-row">
                      <div className="calc-item">
                        <span>Tickets:</span>
                        <strong className="ticket-count">
                          {Math.floor(pago.monto / pago.precio_usdt_ves)}
                        </strong>
                      </div>
                      <div className="calc-item">
                        <span className="label">Precio x 1 USDT:</span>
                        <span>{pago.precio_usdt_ves.toLocaleString()} VES</span>
                      </div>
                      {Number((pago.monto - pago.precio_usdt_ves).toFixed(2)) <
                        0 && (
                        <div
                          className="calc-item"
                          style={{ background: "#fff3cd" }}
                        >
                          <span style={{ color: "#856404" }}>Faltante:</span>
                          <strong style={{ color: "#856404" }}>
                            {(pago.precio_usdt_ves - pago.monto).toFixed(2)} VES
                          </strong>
                        </div>
                      )}
                    </div>
                  )
                )}

                {/* Acciones */}
                <div className="pago-item-actions">
                  <button
                    className="btn-action approve"
                    onClick={() => handleAprobar(pago, false)}
                    disabled={procesando === pago.id}
                  >
                    {procesando === pago.id ? "..." : "✓ Aprobar"}
                  </button>
                  {pago.precio_usdt_ves &&
                    Number((pago.monto - pago.precio_usdt_ves).toFixed(2)) <
                      0 && (
                      <button
                        className="btn-action approve-anyway"
                        onClick={() => {
                          if (
                            window.confirm(
                              `⚠️ El monto (${pago.monto.toFixed(
                                2
                              )} VES) es menor al precio (${pago.precio_usdt_ves.toFixed(
                                2
                              )} VES).\n\n¿Aprobar de todas formas bajo tu responsabilidad?`
                            )
                          ) {
                            handleAprobar(pago, true);
                          }
                        }}
                        disabled={procesando === pago.id}
                        title="Aprobar aunque no alcance el monto"
                      >
                        ⚠️ Aprobar Anyway
                      </button>
                    )}
                  <button
                    className="btn-action reject-icon"
                    onClick={() => handleRechazarClick(pago)}
                    disabled={procesando === pago.id}
                    title="Rechazar Pago"
                  >
                    ✗
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal de Rechazo Profesional */}
      {showRechazoModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h4>Confirmar Rechazo</h4>
              <button
                className="close-modal"
                onClick={() => setShowRechazoModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="pago-summary-mini">
                <p>
                  Vas a rechazar el pago de{" "}
                  <strong>{pagoARechazar?.monto?.toLocaleString()} VES</strong>
                </p>
                <span>Ref: {pagoARechazar?.referencia}</span>
              </div>
              <label>Motivo del rechazo</label>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Ej: Referencia no coincide, monto insuficiente..."
                rows={3}
              />
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowRechazoModal(false)}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button onClick={handleConfirmarRechazo} className="btn-danger">
                Confirmar Rechazo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
